import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Clock, RussianRuble } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, getFullUrl } from '../config';

function EventsView({ onShowEventDetails, onBack }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'online', 'offline'

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/event-occurrences`);
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      const eventsData = await response.json();
      // Filter only enabled, non-cancelled, and upcoming events
      const now = new Date();
      setEvents(eventsData.filter(event => 
        event.enabled && 
        !event.cancelled && 
        new Date(event.startDate) > now
      ));
    } catch (err) {
      console.error('Failed to fetch events:', err);
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isEventUpcoming = (startDate) => {
    return new Date(startDate) > new Date();
  };

  const filteredEvents = events.filter(event => {
    if (filter === 'all') return true;
    return event.type === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-gray-500">{t('loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-600 mb-4">{error}</div>
        <button 
          onClick={fetchEvents}
          className="px-4 py-2 bg-gray-900 text-white rounded hover:bg-black"
        >
          {t('tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">{t('upcomingEvents')}</h3>
        <p className="text-sm sm:text-base text-gray-600">{t('discoverEvents')}</p>
      </div>

      {/* Filter Tabs - Mobile responsive */}
      <div className="mb-4 sm:mb-6 border-b border-gray-200">
        <div className="flex gap-3 sm:gap-6 overflow-x-auto">
          {[
            { key: 'all', label: t('allEvents') },
            { key: 'online', label: t('online') },
            { key: 'offline', label: t('inPerson') }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`pb-3 text-sm sm:text-base font-medium border-b-2 transition-colors whitespace-nowrap ${
                filter === tab.key 
                  ? 'border-gray-900 text-gray-900' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="text-center py-20 text-gray-500">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <div>{t('noEventsAvailable')}</div>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {filteredEvents.map((event) => (
            <div
              key={event.id}
              className="border border-gray-200 rounded p-4 sm:p-6 hover:border-gray-300 transition-colors cursor-pointer"
              onClick={() => onShowEventDetails(event)}
            >
              {/* Mobile: Stack layout, Desktop: Flex layout */}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                {/* Event Image */}
                {event.imageUrl && (
                  <div className="w-full h-32 sm:w-32 sm:h-24 sm:flex-shrink-0">
                    <img 
                      src={getFullUrl(event.imageUrl)} 
                      alt={event.title}
                      className="w-full h-full object-cover rounded border"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        console.warn('Failed to load event image:', event.imageUrl);
                      }}
                    />
                  </div>
                )}

                {/* Event Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 sm:mb-2">
                    <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2 sm:mb-0">{event.title}</h3>
                    <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
                      <span className={`px-2 py-1 rounded ${
                        event.type === 'online' 
                          ? 'bg-gray-100 text-gray-600' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {event.type === 'online' ? t('online') : t('inPerson')}
                      </span>
                      <span className="px-2 py-1 bg-green-50 text-green-700 rounded">
                        {t('upcoming')}
                      </span>
                    </div>
                  </div>

                  <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 line-clamp-2">
                    {event.description}
                  </p>

                  {/* Event metadata - Stack on mobile */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mb-3 sm:mb-0">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="truncate">{formatDate(event.startDate)}</span>
                    </div>
                    
                    {event.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="truncate">{event.location}</span>
                      </div>
                    )}

                    {event.maxAttendees && (
                      <div className="flex items-center gap-1">
                        <span className="truncate">
                          {t('availableSpaces')}: {event.maxAttendees - (event.registrations?.length || 0)}/{event.maxAttendees}
                        </span>
                      </div>
                    )}
                    
                    {event.price && (
                      <div className="flex items-center gap-1">
                        <RussianRuble className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="truncate font-medium">{event.price} ₽</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 sm:mt-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onShowEventDetails(event);
                      }}
                      className="text-sm sm:text-base text-gray-700 hover:text-black font-medium"
                    >
                      {t('viewDetails')} →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default EventsView;